// facility-type-change — correcting a building's type on the Info tab.
//
// 2026-09-16 (info-tab-one-edit-door). The type was set on the onboarding card
// and frozen the moment that card closed, so a building typed wrong stayed
// wrong forever. It became correctable — but only within the limit the data
// model imposed at the time: three detail tables, and the type decided which
// one held the building's square footage. A change ACROSS tables would have
// stranded the footage in a table nothing read for the new type, so it was
// refused here with a sentence naming what was at stake.
//
// 2026-09-17. That limit is gone. There is ONE table, public.facility_details,
// and every column any type can record lives on it. Changing a building's type
// moves nothing and strands nothing: the row stays exactly where it is, and the
// type decides only which of its columns the card ASKS for — see
// DETAIL_FIELD_SPECS in facility-detail.tsx.
//
// So every correction between the five types is now allowed, and the refusal
// that used to carry the model's limit has nothing left to refuse except a
// value that is not a facility type at all. That is the outcome, not an
// oversight: the constraint was removed on purpose, and the check that enforced
// it is kept — narrowed — rather than deleted, so a bad value still cannot
// reach the row.
//
// ⛔ Figures a type is not asked for are HIDDEN, not deleted. A surgery centre
// corrected to Dialysis keeps its sterile square footage on the row; the card
// stops showing it, and correcting the type back shows it again. Nothing this
// screen can do loses a figure.
// ============================================================================

import { NAS_FACILITY_TYPE_LABELS, NAS_FACILITY_TYPE_OPTIONS } from '../../../shared/types';

export type FacilityTypeValue = (typeof NAS_FACILITY_TYPE_OPTIONS)[number];

/** The human label for a type, from the one list the app already uses. */
export function facilityTypeLabel(t: string | null | undefined): string {
  if (!t) return '(not set)';
  return (NAS_FACILITY_TYPE_LABELS as Record<string, string>)[t] ?? t;
}

export function isFacilityType(v: string): v is FacilityTypeValue {
  return (NAS_FACILITY_TYPE_OPTIONS as readonly string[]).includes(v);
}

/**
 * Why a type change is refused, in a sentence the person reading it can act on.
 * Returns null when the change is allowed — which, since the three detail
 * tables became one, is every correction between the five canonical types.
 *
 * ⛔ Names what would happen and who to ask. "Not permitted" is not an answer
 * anyone can do anything with.
 */
export function refusalForTypeChange(
  from: string | null | undefined,
  to: string,
): string | null {
  if (!to || to === from) return null;
  if (!isFacilityType(to)) {
    return `${to} is not one of the five facility types this system recognises.`;
  }
  return null;
}
